// import { Button, Table, TextInput } from "flowbite-react";
import { Box, Button, TextField, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { PasswordCredential } from "../interfaces/ApplicationInterfaces";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { isExpiring } from "../utils/DateTimeHelper";
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { AdminContext } from '../contexts/AdminContext';
import Alert from '@mui/material/Alert';
// import { Alert, Datepicker , Label } from 'flowbite-react';
import dayjs from "dayjs";
import { DatePicker } from '@mui/x-date-pickers';
import { useSnackbar } from 'notistack';
import { IEF_API, IEF_API_POST_ENDPOINT } from "../api/API";

export interface AppSecretsProps {
  secrets: PasswordCredential[];
  appId: string;
  orgId: string;
  reloadAppData: (appId: string) => Promise<void>
}
const sixMonthsFromNow = dayjs(new Date((new Date(Date.now())).setMonth(new Date(Date.now()).getMonth() + 6)))

export function AppSecrets({ secrets, appId, orgId, reloadAppData }: AppSecretsProps) {
  const { adminCredentials, getToken } = useContext(AdminContext);
  const [_secrets, _setSecrets] = useState(secrets);
  const [newSecretValue, setNewSecretValue] = useState("");
  const [newSecretDisplayName, setNewSecretDisplayName] = useState("");
  const [newStartDate, setNewStartDate] = useState<any>(dayjs(new Date(Date.now())));
  const [newEndDate, setNewEndDate] = useState<any>(sixMonthsFromNow);
  const currentSecrets = [];
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const createSecret = async () => {
    setNewSecretValue("");
    let newSecretObj = { displayName: newSecretDisplayName, startDateTime: newStartDate.toISOString(), endDateTime: newEndDate.toISOString() }
    enqueueSnackbar('Creating secret ' + newSecretDisplayName, {
      variant: 'info',
    });
    const body = { applicationId: appId, secret: newSecretObj, orgId: orgId };
    let resp = await IEF_API.POST(
      body,
      IEF_API_POST_ENDPOINT.ApplicationSecrets,
      getToken()
    );

    if (resp.ok) {
      enqueueSnackbar('Secret ' + newSecretDisplayName + ' created', {
        variant: 'success',
      });
      setNewSecretValue(resp.respBody.secret);

      let newSecret: PasswordCredential = {
        displayName: newSecretDisplayName,
        startDateTime: newStartDate.toISOString(),
        endDateTime: newEndDate.toISOString(),
        keyId: "",
        hint: ""
      };
      _setSecrets([..._secrets, newSecret]);
    }
    else {
      enqueueSnackbar(resp.respBody.message, {
        variant: 'error',
      });
    }
  }
  const deleteSecret = async (secretToDelete: PasswordCredential) => {
    setNewSecretValue("");
    enqueueSnackbar('Deleting secret ' + secretToDelete.displayName, {
      variant: 'info',
    });
    const body = { applicationId: appId, secret: secretToDelete, orgId: orgId };
    let resp = await IEF_API.DELETE(
      body,
      IEF_API_POST_ENDPOINT.ApplicationSecrets,
      getToken()
    );

    // const body = {applicationId: appId, secret: };
    // let resp = await fetch(IEF_API.ApplicationSecrets, {    
    //     method: "DELETE",  
    //     headers: {
    //       Authorization: `Bearer ${accessToken}`,
    //     },
    //     body: JSON.stringify(body)
    //   });

    if(resp.ok){
        enqueueSnackbar('Secret '+secretToDelete.displayName+' deleted.', {
        variant: 'success',
      });
      reloadAppData(appId);
    }
    else {
      let errMessage = `Error :`+JSON.stringify(resp);
        enqueueSnackbar(errMessage, {
        variant: 'error',
      });
    }
  }
  for (const secret of _secrets) {
    currentSecrets.push(
      <TableRow key={secret.displayName}>
        <TableCell>
          {isExpiring(secret.endDateTime, 30) ? <WarningAmberRoundedIcon className='h-5 w-5 text-red-500' /> : <CheckRoundedIcon className='h-5 w-5 text-green-500' />}
        </TableCell>
        <TableCell>
          {secret.displayName}
        </TableCell>
        <TableCell>
          {(new Date(secret.startDateTime)).toLocaleDateString()}
        </TableCell>
        <TableCell>
          {(new Date(secret.endDateTime)).toLocaleDateString()}
        </TableCell>
        <TableCell>
          <Button
            className="m-2 w-24"
            variant="outlined"
            onClick={() => deleteSecret(secret)}>
            Delete
          </Button>
        </TableCell>
      </TableRow>
    )
  }

  const writeToClipboard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(appId+"|#|"+newSecretValue);
      enqueueSnackbar('Copied to clipboard', {
        variant: 'success',
      });
    }
    catch {
      enqueueSnackbar('Unable to copy to clipboard', {
        variant: 'error',
      });
    }
  }
  const SecretText = () => {
    return <>
      <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
        <div className="mb-4 mt-2 text-sm text-cyan-700 dark:text-cyan-800">
          {appId+"|#|"+newSecretValue}
        </div>
        <Button
          variant="contained"
          sx={{ml: 3}}
          // className="mr-2 inline-flex items-center rounded-lg bg-cyan-700 px-3 py-1.5 text-center text-xs font-medium text-white hover:bg-cyan-800 focus:ring-4 focus:ring-cyan-300 dark:bg-cyan-800 dark:hover:bg-cyan-900"
          onClick={() => writeToClipboard(newSecretValue)}
        >
          <ContentCopyIcon className="-ml-0.5 mr-2 h-4 w-4" />
          Copy to Clipboard
        </Button>
      </Box>
    </>
  }

  return <div className="flex flex-col">
    <Box sx={{ display: 'flex', flexDirection: 'row', mb: 2, gap:2 }}>
      <TextField
        name="secret-name"
        value={newSecretDisplayName}
        onChange={(e) => setNewSecretDisplayName(e.target.value)}
        required
        label="Secret Name"
      />
      <DatePicker
        sx={{ mb: 2 }}
        label="Start Date"
        minDate={dayjs(new Date(Date.now()))}
        value={dayjs(newStartDate)}
        onChange={(date) => date && setNewStartDate(date)}
      />
      <DatePicker
        sx={{ mb: 2 }}
        label="End Date"
        minDate={dayjs(sixMonthsFromNow)}
        value={dayjs(newEndDate)}
        onChange={(date) => date && setNewEndDate(date)}
      />
      <Button type="submit" variant="contained" sx={{mb: 2}} onClick={createSecret}>Create</Button>
    </Box>
    {newSecretValue !== '' &&
      <Alert color='info' sx={{alignItems: 'center'}}> <SecretText />
        <Typography variant="h6">This secret will not be displayed again! Please copy it for use.</Typography>
      </Alert>}
    {_secrets.length > 0 && <div className="">
      Secrets:
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              {'> 1 Month until Expiration'}
            </TableCell>
            <TableCell>
              Name
            </TableCell>
            <TableCell>
              Start Date
            </TableCell>
            <TableCell>
              End Date
            </TableCell>
            <TableCell>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{currentSecrets}</TableBody>
      </Table>
    </div>}
  </div>
}
